import { t } from "../../i18n";
import { filterModels, type Provider } from "@lumen/shared";
import { LoaderCircle, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { api } from "../../api";
import { ConfirmDangerDialog } from "../../components/shared/ConfirmDangerDialog";
import { ModelSearchPicker } from "../../components/composer/ModelSearchPicker";
import { SettingsPage } from "../settings/ProfilePage";
import { ModelEditor } from "../../components/models/ModelEditor";
import { type RichModel } from "./ModelsPage.utils";
import type { Model } from "@lumen/shared";
import { Link } from "react-router-dom";

export function ModelsPage({
  canWrite,
  canManageCustomModels = false,
}: {
  canWrite: boolean;
  canManageCustomModels?: boolean;
}) {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [query, setQuery] = useState("");
  const [providerId, setProviderId] = useState("all");
  const [capability, setCapability] = useState("all");
  const [tag, setTag] = useState("all");
  const [error, setError] = useState("");
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [moving, setMoving] = useState(false);
  const [deleting, setDeleting] = useState<RichModel | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [titleModelId, setTitleModelId] = useState<string | null>(null);
  const [memoryModelId, setMemoryModelId] = useState<string | null>(null);
  const [defaultModelId, setDefaultModelId] = useState<string | null>(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  useEffect(() => {
    Promise.all([
      api<RichModel[]>("/models/settings"),
      canWrite
        ? api<{
            titleModelId: string | null;
            memoryModelId: string | null;
            defaultModelId: string | null;
          }>("/admin/settings")
        : Promise.resolve(null),
    ])
      .then(([nextModels, settings]) => {
        const nextProviders = [
          ...new Map(
            nextModels.map((model) => [
              model.providerId,
              {
                id: model.providerId,
                name: model.providerName ?? "Provider",
                enabled: model.providerEnabled ?? true,
                models: nextModels.filter(
                  (item) => item.providerId === model.providerId,
                ),
              } as Provider,
            ]),
          ).values(),
        ];
        setProviders(nextProviders);
        if (settings) {
          setTitleModelId(settings.titleModelId);
          setMemoryModelId(settings.memoryModelId);
          setDefaultModelId(settings.defaultModelId);
        }
      })
      .catch((e) => setError(e.message));
  }, [canWrite]);
  const models = useMemo(
    () =>
      providers
        .flatMap((provider) =>
          (provider.models ?? []).map(
            (model) => ({ ...model, providerName: provider.name }) as RichModel,
          ),
        )
        .sort(
          (a, b) =>
            (a.sortOrder ?? Number.MAX_SAFE_INTEGER) -
              (b.sortOrder ?? Number.MAX_SAFE_INTEGER) ||
            a.name.localeCompare(b.name),
        ),
    [providers],
  );
  const tags = [...new Set(models.flatMap((model) => model.tags))].sort();
  const filtered = filterModels(models, {
    providerId: providerId === "all" ? undefined : providerId,
    tag: tag === "all" ? undefined : tag,
    capability: capability === "all" ? undefined : capability,
    query,
  });
  const saveModel = (saved: RichModel) =>
    setProviders((current) =>
      current.map((provider) => ({
        ...provider,
        models: provider.models?.map((item) =>
          item.id === saved.id ? { ...item, ...saved } : item,
        ),
      })),
    );
  const persistOrder = async (ordered: RichModel[]) => {
    setMoving(true);
    setError("");
    const ids = ordered.map((model) => model.id);
    setProviders((current) =>
      current.map((provider) => ({
        ...provider,
        models: provider.models?.map((item) => ({
          ...item,
          sortOrder: ids.indexOf(item.id),
        })) as Model[],
      })),
    );
    try {
      await api("/models/order", {
        method: "PUT",
        body: JSON.stringify({ modelIds: ids }),
      });
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Model order could not be saved",
      );
    } finally {
      setMoving(false);
    }
  };
  const move = (id: string, target: number) => {
    const from = models.findIndex((model) => model.id === id);
    if (from < 0) return;
    const ordered = [...models];
    const [item] = ordered.splice(from, 1);
    ordered.splice(Math.max(0, Math.min(target, ordered.length)), 0, item);
    void persistOrder(ordered);
  };
  const reset = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    setError("");
    try {
      const updated = await api<RichModel>(`/models/${deleting.id}`, {
        method: "DELETE",
      });
      saveModel({ ...deleting, ...updated });
      setDeleting(null);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Model config could not be reset",
      );
    } finally {
      setDeleteBusy(false);
    }
  };
  const saveSetting = async (
    key: "titleModelId" | "memoryModelId" | "defaultModelId",
    value: string,
  ) => {
    const next = value || null;
    if (key === "titleModelId") setTitleModelId(next);
    else if (key === "memoryModelId") setMemoryModelId(next);
    else setDefaultModelId(next);
    setSettingsSaving(true);
    setError("");
    try {
      await api("/admin/settings", {
        method: "PATCH",
        body: JSON.stringify({ [key]: next }),
      });
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Model setting could not be saved",
      );
    } finally {
      setSettingsSaving(false);
    }
  };
  const availableModels = models.filter(
    (model) =>
      model.enabled &&
      model.userEnabled !== false &&
      model.scope !== "personal" &&
      providers.find((provider) => provider.id === model.providerId)?.enabled,
  );
  return (
    <SettingsPage
      title={t("Models")}
      description={
        canWrite
          ? "Curate model presentation, capabilities, tool access, ordering, and availability independently from provider setup."
          : "Choose which global models are available to you, and review your personal models."
      }
    >
      {canWrite && (
        <section className="panel mb-5 grid gap-5 p-4 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <span className="label">{t("Default model")}</span>
            <ModelSearchPicker
              models={availableModels}
              value={defaultModelId ?? ""}
              disabled={settingsSaving}
              emptyLabel="Use the first available model"
              onChange={(value) => void saveSetting("defaultModelId", value)}
            />
            <p className="mt-2 text-xs text-stone-400">
              {t(
                "Used for new conversations unless a user chooses a personal default.",
              )}
            </p>
          </div>
          <div>
            <span className="label">{t("Memory model")}</span>
            <ModelSearchPicker
              models={availableModels}
              value={memoryModelId ?? ""}
              disabled={settingsSaving}
              emptyLabel={t("Do not populate memories automatically")}
              onChange={(value) => void saveSetting("memoryModelId", value)}
            />
            <p className="mt-2 text-xs text-stone-400">
              {t(
                "Reviews completed interactions and extracts durable memories for users who opted in.",
              )}
            </p>
          </div>
          <div>
            <span className="label">{t("Conversation title model")}</span>
            <ModelSearchPicker
              models={availableModels}
              value={titleModelId ?? ""}
              disabled={settingsSaving}
              emptyLabel="Use the active chat model"
              onChange={(value) => void saveSetting("titleModelId", value)}
            />
            <p className="mt-2 text-xs text-stone-400">
              {t("Generates concise names after a conversation begins.")}
            </p>
          </div>
          {settingsSaving && (
            <span className="inline-flex items-center gap-2 text-xs text-stone-400">
              <LoaderCircle className="animate-spin" size={14} /> {t("Saving…")}
            </span>
          )}
        </section>
      )}
      {canManageCustomModels && (
        <div className="mb-5 flex items-center justify-between gap-3 rounded-xl border border-stone-200 p-4 dark:border-white/10">
          <div>
            <h2 className="font-semibold">{t("Custom models")}</h2>
            <p className="text-sm text-stone-500">
              {t(
                "Create routing and fallback models that appear alongside normal\n              models.",
              )}
            </p>
          </div>
          <Link className="button" to="/settings/custom-models">
            {t("Manage custom models")}
          </Link>
        </div>
      )}
      <div className="panel mb-5 grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
        <label className="relative sm:col-span-2 lg:col-span-1">
          <Search className="absolute top-3 left-3 text-stone-400" size={16} />
          <input
            className="field pl-9"
            placeholder={t("Search models")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <select
          className="field"
          aria-label={t("Provider")}
          value={providerId}
          onChange={(e) => setProviderId(e.target.value)}
        >
          <option value="all">{t("All providers")}</option>
          {providers.map((provider) => (
            <option key={provider.id} value={provider.id}>
              {provider.name}
            </option>
          ))}
        </select>
        <select
          className="field"
          aria-label={t("Capability")}
          value={capability}
          onChange={(e) => setCapability(e.target.value)}
        >
          <option value="all">{t("All capabilities")}</option>
          <option value="vision">{t("Image input")}</option>
          <option value="text-output">{t("Text output")}</option>
          <option value="image-output">{t("Image output")}</option>
          <option value="tools">{t("Tools enabled")}</option>
        </select>
        <select
          className="field"
          aria-label={t("Tag")}
          value={tag}
          onChange={(e) => setTag(e.target.value)}
        >
          <option value="all">{t("All tags")}</option>
          {tags.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </div>
      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}
      {canWrite && (
        <p className="mb-3 text-xs text-stone-400">
          {t(
            "Drag the grip on desktop or use the arrow buttons to reorder. Order is saved immediately.",
          )}
        </p>
      )}
      <div className="space-y-3">
        {filtered.map((model) => {
          const index = models.findIndex((item) => item.id === model.id);
          return (
            <ModelEditor
              key={model.id}
              model={model}
              provider={providers.find(
                (provider) => provider.id === model.providerId,
              )}
              allTags={tags}
              canWrite={canWrite}
              moving={moving}
              canMoveUp={index > 0}
              canMoveDown={index >= 0 && index < models.length - 1}
              onSaved={saveModel}
              onReset={setDeleting}
              onMoveTop={() => move(model.id, 0)}
              onMove={(direction) => move(model.id, index + direction)}
              onDragStart={() => setDraggedId(model.id)}
              onDrop={() => {
                if (draggedId && draggedId !== model.id) move(draggedId, index);
                setDraggedId(null);
              }}
            />
          );
        })}
      </div>
      {filtered.length === 0 && (
        <div className="py-14 text-center text-sm text-stone-400">
          {t("No models match these filters.")}
        </div>
      )}
      {deleting && (
        <ConfirmDangerDialog
          title={`Reset ${deleting.name}?`}
          description="This removes the local name, description, icon, tags, capability overrides, and tool settings. The model is made available again."
          confirmLabel="Reset model config"
          busy={deleteBusy}
          onCancel={() => setDeleting(null)}
          onConfirm={() => void reset()}
        />
      )}
    </SettingsPage>
  );
}
